import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { aiAPI } from '../services/api';
import ReportIssueButton from './ReportIssueButton';

function CourseAIQuestions({ courseId }) {
  const [questionsData, setQuestionsData] = useState([]);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedChapters, setExpandedChapters] = useState({});
  const [expandedAssets, setExpandedAssets] = useState({});

  useEffect(() => {
    if (courseId) {
      fetchQuestions();
    }
  }, [courseId]);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      console.log('Fetching AI questions for course:', courseId);
      const response = await aiAPI.getCourseQuestions(courseId);
      console.log('AI Questions Response:', response.data);
      setQuestionsData(response.data.data || []);
      setTotalQuestions(response.data.totalQuestions || 0);
      setError('');
    } catch (err) {
      console.error('Error fetching AI questions:', err);
      setError(err.response?.data?.message || 'Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  const toggleChapter = (chapterId) => {
    setExpandedChapters(prev => ({
      ...prev,
      [chapterId]: !prev[chapterId]
    }));
  };

  const toggleAsset = (assetId) => {
    setExpandedAssets(prev => ({
      ...prev,
      [assetId]: !prev[assetId]
    }));
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#718096' }}>
        Loading AI questions...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        padding: '20px', 
        margin: '20px',
        background: '#fed7d7', 
        color: '#742a2a',
        borderRadius: '8px'
      }}>
        {error}
      </div>
    );
  }

  if (totalQuestions === 0) {
    return (
      <div style={{ 
        padding: '60px', 
        textAlign: 'center', 
        color: '#718096'
      }}>
        <p style={{ fontSize: '48px', marginBottom: '16px' }}>🤖</p>
        <h3 style={{ fontSize: '20px', marginBottom: '8px', color: '#2d3748' }}>
          No AI Questions Yet
        </h3>
        <p style={{ fontSize: '14px' }}>
          Students haven't asked any questions using the AI assistant.
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', background: '#f7fafc', minHeight: '400px' }}>
      {/* Header */}
      <div style={{ 
        marginBottom: '24px',
        padding: '20px',
        background: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#1a202c', marginBottom: '4px' }}>
              AI Questions Analytics
            </h2>
            <p style={{ fontSize: '14px', color: '#718096' }}>
              Questions asked by students using the AI assistant
            </p>
          </div>
          <div style={{
            padding: '12px 24px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#fff',
            borderRadius: '8px',
            fontSize: '18px',
            fontWeight: '600'
          }}>
            {totalQuestions} {totalQuestions === 1 ? 'Question' : 'Questions'}
          </div>
        </div>
      </div>

      {/* Questions grouped by chapter and asset */}
      {questionsData.map((chapter, chapterIdx) => (
        <div 
          key={chapter.chapterId}
          style={{
            marginBottom: '16px',
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            overflow: 'hidden'
          }}
        >
          {/* Chapter Header */}
          <div
            onClick={() => toggleChapter(chapter.chapterId)}
            style={{
              padding: '16px 20px',
              background: expandedChapters[chapter.chapterId] ? '#edf2f7' : '#ffffff',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: expandedChapters[chapter.chapterId] ? '1px solid #e2e8f0' : 'none'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{
                fontSize: '14px',
                fontWeight: '700',
                color: '#667eea',
                backgroundColor: '#eef2ff',
                padding: '4px 10px',
                borderRadius: '6px',
                minWidth: '45px',
                textAlign: 'center'
              }}>
                #{chapterIdx + 1}
              </span>
              <span style={{ fontSize: '16px' }}>
                {expandedChapters[chapter.chapterId] ? '📖' : '📕'}
              </span>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1a202c', margin: 0 }}>
                {chapter.chapterTitle}
              </h3>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{
                padding: '4px 12px',
                background: '#667eea',
                color: '#fff',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '600'
              }}>
                {chapter.assets.reduce((sum, asset) => sum + asset.questions.length, 0)} questions
              </span>
              <span style={{ fontSize: '20px', color: '#718096' }}>
                {expandedChapters[chapter.chapterId] ? '▼' : '▶'}
              </span>
            </div>
          </div>

          {/* Assets */}
          {expandedChapters[chapter.chapterId] && (
            <div style={{ padding: '12px' }}>
              {chapter.assets.map((asset, assetIdx) => (
                <div
                  key={asset.assetId}
                  style={{
                    marginBottom: '12px',
                    background: '#f7fafc',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0'
                  }}
                >
                  {/* Asset Header */}
                  <div
                    onClick={() => toggleAsset(asset.assetId)}
                    style={{
                      padding: '12px 16px',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{
                        fontSize: '12px',
                        fontWeight: '700',
                        color: '#718096',
                        backgroundColor: '#e2e8f0',
                        padding: '3px 8px',
                        borderRadius: '4px',
                        minWidth: '35px',
                        textAlign: 'center'
                      }}>
                        {assetIdx + 1}
                      </span>
                      <span style={{ fontSize: '14px' }}>
                        {asset.assetType === 'note' && '📋'}
                        {asset.assetType === 'video' && '🎥'}
                        {asset.assetType === 'document' && '📄'}
                        {asset.assetType === 'quiz' && '📝'}
                        {asset.assetType === 'flashcard' && '🎴'}
                        {asset.assetType === 'interview' && '🎤'}
                        {asset.assetType === 'link' && '🔗'}
                        {asset.assetType === 'transcript' && '📜'}
                        {asset.assetType === 'markdown' && '📝'}
                      </span>
                      <span style={{ fontSize: '16px', fontWeight: '600', color: '#2d3748' }}>
                        {asset.assetTitle}
                      </span>
                      <span style={{
                        padding: '2px 8px',
                        background: '#e2e8f0',
                        color: '#718096',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: '600',
                        textTransform: 'capitalize'
                      }}>
                        {asset.assetType}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{
                        padding: '3px 10px',
                        background: '#ffffff',
                        color: '#667eea',
                        borderRadius: '10px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {asset.questions.length} {asset.questions.length === 1 ? 'question' : 'questions'}
                      </span>
                      <span style={{ fontSize: '16px', color: '#718096' }}>
                        {expandedAssets[asset.assetId] ? '▼' : '▶'}
                      </span>
                    </div>
                  </div>

                  {/* Questions */}
                  {expandedAssets[asset.assetId] && (
                    <div style={{ padding: '0 16px 12px 16px' }}>
                      {asset.questions.map((q, idx) => (
                        <div
                          key={q._id}
                          style={{
                            padding: '14px',
                            background: '#ffffff',
                            borderRadius: '8px',
                            marginBottom: '8px',
                            border: '1px solid #e2e8f0'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '14px' }}>
                                {q.wasSuggested ? '💡' : '❓'}
                              </span>
                              <span style={{
                                padding: '2px 8px',
                                background: q.wasSuggested ? '#fef5e7' : '#e8f4f8',
                                color: q.wasSuggested ? '#b7791f' : '#2c5282',
                                borderRadius: '6px',
                                fontSize: '11px',
                                fontWeight: '600'
                              }}>
                                {q.wasSuggested ? 'Suggested' : 'Typed'}
                              </span>
                              <ReportIssueButton
                                sourceType="aiQuestion"
                                sourceId={q._id}
                                questionPath="question"
                                questionId={q._id}
                                questionIndex={idx}
                                questionData={{
                                  question: q.question,
                                  questionType: 'ai-generated',
                                  aiResponse: q.aiResponse,
                                  wasSuggested: q.wasSuggested
                                }}
                                metadata={{
                                  sourceName: `AI Question - ${asset.assetTitle}`,
                                  courseId: courseId,
                                  chapterId: chapter.chapterId,
                                  chapterTitle: chapter.chapterTitle,
                                  assetTitle: asset.assetTitle,
                                  assetType: 'ai-question',
                                  questionNumber: idx + 1,
                                  totalQuestions: asset.questions?.length || 0,
                                  askedAt: q.askedAt,
                                  reportedFrom: 'course-ai-questions',
                                  timestamp: new Date().toISOString()
                                }}
                                size="icon-only"
                              />
                            </div>
                            <span style={{ fontSize: '12px', color: '#a0aec0' }}>
                              {formatDate(q.askedAt)}
                            </span>
                          </div>
                          <p style={{
                            fontSize: '15px',
                            color: '#1a202c',
                            margin: '0 0 10px 0',
                            lineHeight: '1.6',
                            fontWeight: '600'
                          }}>
                            {q.question}
                          </p>
                          {q.aiResponse && (
                            <div style={{
                              padding: '12px',
                              background: '#f0fdf4',
                              borderRadius: '6px',
                              marginBottom: '10px',
                              border: '1px solid #86efac'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                                <span style={{ fontSize: '14px' }}>🤖</span>
                                <span style={{ fontSize: '12px', fontWeight: '600', color: '#16a34a' }}>AI Response:</span>
                              </div>
                              <div style={{
                                fontSize: '14px',
                                color: '#166534',
                                lineHeight: '1.6'
                              }}>
                                <ReactMarkdown 
                                  remarkPlugins={[remarkGfm]}
                                  components={{
                                    // Style code blocks
                                    code: ({node, inline, className, children, ...props}) => {
                                      return inline ? (
                                        <code style={{
                                          backgroundColor: '#d1fae5',
                                          padding: '2px 6px',
                                          borderRadius: '3px',
                                          fontSize: '13px',
                                          fontFamily: 'monospace'
                                        }} {...props}>
                                          {children}
                                        </code>
                                      ) : (
                                        <pre style={{
                                          backgroundColor: '#dcfce7',
                                          padding: '12px',
                                          borderRadius: '6px',
                                          overflow: 'auto',
                                          marginTop: '8px',
                                          marginBottom: '8px'
                                        }}>
                                          <code style={{
                                            fontFamily: 'monospace',
                                            fontSize: '13px'
                                          }} {...props}>
                                            {children}
                                          </code>
                                        </pre>
                                      );
                                    },
                                    // Style paragraphs
                                    p: ({children}) => (
                                      <p style={{ margin: '8px 0' }}>{children}</p>
                                    ),
                                    // Style lists
                                    ul: ({children}) => (
                                      <ul style={{ marginLeft: '20px', marginTop: '8px', marginBottom: '8px' }}>{children}</ul>
                                    ),
                                    ol: ({children}) => (
                                      <ol style={{ marginLeft: '20px', marginTop: '8px', marginBottom: '8px' }}>{children}</ol>
                                    )
                                  }}
                                >
                                  {q.aiResponse}
                                </ReactMarkdown>
                              </div>
                            </div>
                          )}
                          <div style={{
                            padding: '8px 12px',
                            background: '#f7fafc',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <span style={{ fontSize: '14px' }}>👤</span>
                            <div>
                              <span style={{ fontSize: '13px', fontWeight: '600', color: '#2d3748' }}>
                                {q.student.name}
                              </span>
                              <span style={{ fontSize: '12px', color: '#718096', marginLeft: '8px' }}>
                                {q.student.email}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default CourseAIQuestions;
